'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import type { VehicleRange } from './vehicleFilters';

type Thumb = keyof VehicleRange;

const THUMBS: Thumb[] = ['min', 'max'];

const THUMB_NAMES: Record<Thumb, string> = { min: 'von', max: 'bis' };

export type VehicleRangeSliderProps = {
    /** Names both thumbs: „Preis von“, „Preis bis“. */
    legend: string;
    /** The ends of the scale. */
    bounds: VehicleRange;
    step: number;
    value: VehicleRange;
    onChange: (value: VehicleRange) => void;
    /** One thumb as text, for screen readers. */
    valueText: (value: number) => string;
};

/** A thumb stops where the other one stands. */
function clampThumb(thumb: Thumb, next: number, value: VehicleRange): number {
    return thumb === 'min' ? Math.min(next, value.max) : Math.max(next, value.min);
}

/**
 * Two native range inputs on one track, so each thumb keeps keyboard and screen
 * reader support. The thumb moved last lies on top: thumbs that meet can part again.
 */
export function VehicleRangeSlider({ legend, bounds, step, value, onChange, valueText }: VehicleRangeSliderProps) {
    const [top, setTop] = useState<Thumb>('max');

    const moveThumb = (thumb: Thumb, next: number) => {
        const position = clampThumb(thumb, next, value);
        if (position === value[thumb]) return;

        setTop(thumb);
        onChange({ ...value, [thumb]: position });
    };

    return (
        <div className="vehicle-filter-range">
            {THUMBS.map((thumb) => (
                <input
                    key={thumb}
                    type="range"
                    className={cn('vehicle-filter-range__input', top === thumb && 'vehicle-filter-range__input--top')}
                    min={bounds.min}
                    max={bounds.max}
                    step={step}
                    value={value[thumb]}
                    aria-label={`${legend} ${THUMB_NAMES[thumb]}`}
                    aria-valuetext={valueText(value[thumb])}
                    onChange={(event) => moveThumb(thumb, Number(event.target.value))}
                />
            ))}
        </div>
    );
}
